import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { api, apiEndpoints } from '../../utils/apiService';

export type ReservationLocationMode = 'PEER' | 'FALLBACK';

export interface ReservationLocationReadiness {
  store_id: number;
  store_name: string;
  takes_reservations: boolean;
  has_timezone: boolean;
  opentable_connected: boolean;
  opentable_rid: string | null;
  ready: boolean;
  blockers: string[];
}

export interface ReservationLocationMembership {
  id: number | null;
  store_id: number;
  store_name: string;
  mode: ReservationLocationMode;
  rank: number;
  is_active: boolean;
  voice_label: string;
  aliases: string[];
  reservations_enabled?: boolean;
  reservation_unavailable_notice?: string;
  readiness?: ReservationLocationReadiness;
}

export interface ReservationLocationScope {
  configured: boolean;
  id: number | null;
  name: string;
  is_active: boolean;
  max_peers: number;
  memberships: ReservationLocationMembership[];
}

interface ReservationLocationScopeSectionProps {
  storeId: number;
  storeName: string;
  storeOptions: SearchableSelectOption[];
  embedded?: boolean;
}

function preventParentFormSubmit(event: React.KeyboardEvent) {
  if (event.key !== 'Enter') return;
  const target = event.target as HTMLElement;
  if (target.tagName === 'TEXTAREA' || target.closest('button')) return;
  event.preventDefault();
}

interface DraftMember {
  key: string;
  storeId: number;
  storeName: string;
  voiceLabel: string;
  aliases: string;
  reservationsEnabled: boolean;
  unavailableNotice: string;
  readiness?: ReservationLocationReadiness;
}

const emptyScope = (
  storeId: number,
  storeName: string,
): ReservationLocationScope => ({
  configured: false,
  id: null,
  name: '',
  is_active: false,
  max_peers: 4,
  memberships: [
    {
      id: null,
      store_id: storeId,
      store_name: storeName,
      mode: 'PEER',
      rank: 0,
      is_active: true,
      voice_label: storeName,
      aliases: [],
    },
  ],
});

function formatApiError(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const payload = data as Record<string, unknown>;
  const error = payload.error ?? payload.detail ?? payload.message;
  if (typeof error === 'string' && error.trim()) return error;
  if (Array.isArray(error)) {
    return (
      error.filter((item) => typeof item === 'string').join(' ') || fallback
    );
  }
  if (error && typeof error === 'object') {
    const messages = Object.values(error as Record<string, unknown>)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter(
        (value): value is string =>
          typeof value === 'string' && Boolean(value.trim()),
      );
    if (messages.length) return messages.join(' ');
  }
  return fallback;
}

async function readResponseJson(response: Response | null): Promise<unknown> {
  if (!response) return {};
  return response.json().catch(() => ({}));
}

function parseAliases(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function toDraftMembers(
  memberships: ReservationLocationMembership[],
  entryStoreId: number,
  entryStoreName: string,
): { entry: DraftMember; peers: DraftMember[] } {
  const peers = memberships
    .filter((item) => item.mode === 'PEER')
    .sort((left, right) => left.rank - right.rank);
  const entryMembership =
    peers.find((item) => item.store_id === entryStoreId) ??
    peers[0] ??
    emptyScope(entryStoreId, entryStoreName).memberships[0];
  return {
    entry: {
      key: 'entry',
      storeId: entryStoreId,
      storeName: entryMembership.store_name || entryStoreName,
      voiceLabel: entryMembership.voice_label || entryStoreName,
      aliases: (entryMembership.aliases || []).join(', '),
      reservationsEnabled: true,
      unavailableNotice: '',
      readiness: entryMembership.readiness,
    },
    peers: peers
      .filter((item) => item.store_id !== entryStoreId)
      .map((item, index) => ({
        key: `peer-${item.store_id}-${index}`,
        storeId: item.store_id,
        storeName: item.store_name,
        voiceLabel: item.voice_label,
        aliases: (item.aliases || []).join(', '),
        reservationsEnabled: item.reservations_enabled ?? true,
        unavailableNotice: item.reservation_unavailable_notice || '',
        readiness: item.readiness,
      })),
  };
}

export default function ReservationLocationScopeSection({
  storeId,
  storeName,
  storeOptions,
  embedded = false,
}: ReservationLocationScopeSectionProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configured, setConfigured] = useState(false);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [syncPeerScopes, setSyncPeerScopes] = useState(true);
  const [maxPeers, setMaxPeers] = useState(4);
  const [entry, setEntry] = useState<DraftMember>(() => ({
    key: 'entry',
    storeId,
    storeName,
    voiceLabel: storeName,
    aliases: '',
    reservationsEnabled: true,
    unavailableNotice: '',
  }));
  const [peers, setPeers] = useState<DraftMember[]>([]);
  const [fallbackMemberships, setFallbackMemberships] = useState<
    ReservationLocationMembership[]
  >([]);

  const loadScope = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setConfirmRemove(false);
    try {
      const response = await api.get(
        apiEndpoints.getReservationLocationScope(storeId),
      );
      const data = (await readResponseJson(response)) as
        | ReservationLocationScope
        | Record<string, unknown>;
      if (!response?.ok) {
        setError(formatApiError(data, 'Failed to load reservation locations.'));
        const fallback = emptyScope(storeId, storeName);
        const draft = toDraftMembers(fallback.memberships, storeId, storeName);
        setConfigured(false);
        setName('');
        setIsActive(false);
        setMaxPeers(4);
        setEntry(draft.entry);
        setPeers([]);
        setFallbackMemberships([]);
        return;
      }
      const scope = data as ReservationLocationScope;
      const draft = toDraftMembers(scope.memberships || [], storeId, storeName);
      setConfigured(Boolean(scope.configured));
      setName(scope.name || '');
      setIsActive(Boolean(scope.is_active));
      setMaxPeers(scope.max_peers || 4);
      setEntry(draft.entry);
      setPeers(draft.peers);
      setFallbackMemberships(
        (scope.memberships || []).filter((item) => item.mode === 'FALLBACK'),
      );
    } catch {
      setError('Failed to load reservation locations.');
    } finally {
      setLoading(false);
    }
  }, [storeId, storeName]);

  useEffect(() => {
    loadScope();
  }, [loadScope]);

  const selectedStoreIds = useMemo(
    () =>
      new Set([storeId, ...peers.map((item) => item.storeId).filter(Boolean)]),
    [peers, storeId],
  );

  const availableStoreOptions = useMemo(
    () => storeOptions.filter((option) => Number(option.value) !== storeId),
    [storeId, storeOptions],
  );

  const remainingFallbacks = useMemo(
    () =>
      fallbackMemberships.filter(
        (item) => !selectedStoreIds.has(item.store_id),
      ),
    [fallbackMemberships, selectedStoreIds],
  );

  const peerCount = peers.filter((item) => item.storeId).length + 1;
  const canAddPeer = peerCount < maxPeers;

  const updatePeer = (key: string, updates: Partial<DraftMember>) => {
    setPeers((current) =>
      current.map((item) =>
        item.key === key ? { ...item, ...updates } : item,
      ),
    );
  };

  const addPeer = () => {
    if (!canAddPeer) return;
    const unused = availableStoreOptions.find(
      (option) => !selectedStoreIds.has(Number(option.value)),
    );
    setPeers((current) => [
      ...current,
      {
        key: `peer-new-${Date.now()}`,
        storeId: unused ? Number(unused.value) : 0,
        storeName: unused?.label || '',
        voiceLabel: unused
          ? String(unused.label).replace(/\s+\(ID: \d+\)$/, '')
          : '',
        aliases: '',
        reservationsEnabled: true,
        unavailableNotice: '',
      },
    ]);
  };

  const removePeer = (key: string) => {
    setPeers((current) => current.filter((item) => item.key !== key));
  };

  const handleSave = async () => {
    const peerMembers = peers.filter((item) => item.storeId);
    if (peerMembers.length < 1) {
      setError('Add at least one other location to enable the new cross-sell.');
      return;
    }
    const closedWithoutNotice = peerMembers.find(
      (item) => !item.reservationsEnabled && !item.unavailableNotice.trim(),
    );
    if (closedWithoutNotice) {
      setError(
        `Add a closure notice for ${closedWithoutNotice.voiceLabel || closedWithoutNotice.storeName}.`,
      );
      return;
    }
    const peerStoreIds = new Set([
      storeId,
      ...peerMembers.map((item) => item.storeId),
    ]);
    const keptFallbacks = fallbackMemberships.filter(
      (item) => !peerStoreIds.has(item.store_id),
    );
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.put(
        apiEndpoints.updateReservationLocationScope(storeId),
        {
          name,
          is_active: isActive,
          sync_peer_scopes: syncPeerScopes,
          memberships: [
            {
              store_id: storeId,
              mode: 'PEER',
              rank: 0,
              is_active: true,
              voice_label: entry.voiceLabel,
              aliases: parseAliases(entry.aliases),
              reservations_enabled: true,
              reservation_unavailable_notice: '',
            },
            ...peerMembers.map((item, index) => ({
              store_id: item.storeId,
              mode: 'PEER',
              rank: index + 1,
              is_active: true,
              voice_label: item.voiceLabel,
              aliases: parseAliases(item.aliases),
              reservations_enabled: item.reservationsEnabled,
              reservation_unavailable_notice: item.reservationsEnabled
                ? ''
                : item.unavailableNotice.trim(),
            })),
            ...keptFallbacks.map((item) => ({
              store_id: item.store_id,
              mode: 'FALLBACK',
              rank: item.rank,
              is_active: item.is_active,
              voice_label: item.voice_label,
              aliases: item.aliases,
              reservations_enabled: item.reservations_enabled ?? true,
              reservation_unavailable_notice:
                item.reservation_unavailable_notice || '',
            })),
          ],
        },
      );
      const data = await readResponseJson(response);
      if (!response?.ok) {
        setError(formatApiError(data, 'Failed to save reservation locations.'));
        return;
      }
      const scope = data as ReservationLocationScope;
      const draft = toDraftMembers(scope.memberships || [], storeId, storeName);
      setConfigured(Boolean(scope.configured));
      setName(scope.name || '');
      setIsActive(Boolean(scope.is_active));
      setMaxPeers(scope.max_peers || 4);
      setEntry(draft.entry);
      setPeers(draft.peers);
      setFallbackMemberships(
        (scope.memberships || []).filter((item) => item.mode === 'FALLBACK'),
      );
      setSuccess(
        scope.is_active
          ? 'Multi-location reservations are active.'
          : 'Reservation locations saved. Activate when every location is ready.',
      );
    } catch {
      setError('Failed to save reservation locations.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!configured) {
      setPeers([]);
      setConfirmRemove(false);
      return;
    }
    setRemoving(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.delete(
        apiEndpoints.deleteReservationLocationScope(storeId, syncPeerScopes),
      );
      if (!response?.ok && response?.status !== 404) {
        const data = await readResponseJson(response);
        setError(
          formatApiError(data, 'Failed to remove reservation locations.'),
        );
        return;
      }
      const fallback = emptyScope(storeId, storeName);
      const draft = toDraftMembers(fallback.memberships, storeId, storeName);
      setConfigured(false);
      setName('');
      setIsActive(false);
      setEntry(draft.entry);
      setPeers([]);
      setFallbackMemberships([]);
      setConfirmRemove(false);
      setSuccess(
        syncPeerScopes
          ? 'Removed the multi-location reservation group from this store and matching locations.'
          : 'Removed the multi-location reservation group from this store only.',
      );
    } catch {
      setError('Failed to remove reservation locations.');
    } finally {
      setRemoving(false);
    }
  };

  const statusLabel = configured
    ? isActive
      ? 'Active'
      : 'Saved, inactive'
    : 'Not configured';

  return (
    <section
      className={
        embedded
          ? 'mt-5 border-t border-gray-200 pt-5 dark:border-gray-700'
          : 'rounded-2xl border border-gray-200 bg-white/70 p-5 shadow-sm dark:border-gray-700 dark:bg-white/[0.03] md:p-6'
      }
      onKeyDown={preventParentFormSubmit}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
            Multi-location reservations
          </h4>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            Link this store with another location so callers can book either
            place. This is the new peer cross-sell. The Enable Cross-Selling
            toggle above only controls the older “offer another location when
            this one is full” fallback.
          </p>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
            isActive && configured
              ? 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-300'
              : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300'
          }`}
        >
          {statusLabel}
        </span>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-gray-500">
          Loading reservation locations...
        </p>
      ) : (
        <div className="mt-5 space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Group name (optional)</Label>
              <Input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={`${storeName} locations`}
              />
            </div>
          </div>

          <div className="space-y-3">
            <LocationCard
              title="This location"
              member={entry}
              locked
              readiness={entry.readiness}
              onVoiceLabelChange={(value) =>
                setEntry((current) => ({ ...current, voiceLabel: value }))
              }
              onAliasesChange={(value) =>
                setEntry((current) => ({ ...current, aliases: value }))
              }
            />
            {peers.map((peer, index) => (
              <LocationCard
                key={peer.key}
                title={`Other location ${index + 1}`}
                member={peer}
                storeOptions={availableStoreOptions.filter(
                  (option) =>
                    Number(option.value) === peer.storeId ||
                    !selectedStoreIds.has(Number(option.value)),
                )}
                readiness={peer.readiness}
                onStoreChange={(value) => {
                  const option = storeOptions.find(
                    (item) => String(item.value) === value,
                  );
                  const nextName = option
                    ? String(option.label).replace(/\s+\(ID: \d+\)$/, '')
                    : '';
                  updatePeer(peer.key, {
                    storeId: value ? Number(value) : 0,
                    storeName: nextName,
                    voiceLabel: peer.voiceLabel || nextName,
                  });
                }}
                onVoiceLabelChange={(value) =>
                  updatePeer(peer.key, { voiceLabel: value })
                }
                onAliasesChange={(value) =>
                  updatePeer(peer.key, { aliases: value })
                }
                onReservationsEnabledChange={(value) =>
                  updatePeer(peer.key, { reservationsEnabled: value })
                }
                onUnavailableNoticeChange={(value) =>
                  updatePeer(peer.key, { unavailableNotice: value })
                }
                onRemove={() => removePeer(peer.key)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={addPeer}
            disabled={!canAddPeer}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-white/[0.03]"
          >
            Add location
          </button>

          {remainingFallbacks.length > 0 && (
            <p className="text-sm text-gray-500">
              This store also has {remainingFallbacks.length} legacy fallback
              location{remainingFallbacks.length === 1 ? '' : 's'}. Those will
              be kept when you save.
            </p>
          )}

          <label className="flex items-start gap-3 text-sm text-gray-800 dark:text-gray-100">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span>
              Activate this group. Every location must take reservations and
              have a connected OpenTable RID.
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm text-gray-800 dark:text-gray-100">
            <input
              type="checkbox"
              checked={syncPeerScopes}
              onChange={(event) => setSyncPeerScopes(event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span>
              Also set this up on the other locations so callers who dial those
              numbers see the same group. Remove group uses this too: it tears
              down the matching group on those locations.
            </span>
          </label>

          {error && (
            <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-600">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-600">
              {success}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3 text-sm text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save reservation locations'}
            </button>
            {(configured || peers.length > 0) &&
              (confirmRemove ? (
                <>
                  <button
                    type="button"
                    onClick={handleRemove}
                    disabled={removing}
                    className="inline-flex items-center justify-center rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600 hover:bg-error-100 disabled:opacity-60"
                  >
                    {removing ? 'Removing...' : 'Confirm remove'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmRemove(false)}
                    className="inline-flex items-center justify-center rounded-lg px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 dark:text-gray-300"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRemove(true)}
                  className="inline-flex items-center justify-center rounded-lg px-4 py-3 text-sm text-error-600 hover:bg-error-50"
                >
                  Remove group
                </button>
              ))}
          </div>
        </div>
      )}
    </section>
  );
}

function LocationCard({
  title,
  member,
  locked = false,
  storeOptions = [],
  readiness,
  onStoreChange,
  onVoiceLabelChange,
  onAliasesChange,
  onReservationsEnabledChange,
  onUnavailableNoticeChange,
  onRemove,
}: {
  title: string;
  member: DraftMember;
  locked?: boolean;
  storeOptions?: SearchableSelectOption[];
  readiness?: ReservationLocationReadiness;
  onStoreChange?: (value: string) => void;
  onVoiceLabelChange: (value: string) => void;
  onAliasesChange: (value: string) => void;
  onReservationsEnabledChange?: (value: boolean) => void;
  onUnavailableNoticeChange?: (value: string) => void;
  onRemove?: () => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {title}
        </p>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-error-600 hover:underline"
          >
            Remove
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <Label>Location</Label>
          {locked ? (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
              {member.storeName}
            </p>
          ) : (
            <SearchableSelect
              options={storeOptions}
              value={member.storeId || ''}
              onChange={(value) => onStoreChange?.(value)}
              placeholder="Select a store..."
            />
          )}
        </div>
        <div>
          <Label>Voice label</Label>
          <Input
            type="text"
            value={member.voiceLabel}
            onChange={(event) => onVoiceLabelChange(event.target.value)}
            placeholder="Downtown"
          />
        </div>
        <div>
          <Label>Aliases (comma-separated)</Label>
          <Input
            type="text"
            value={member.aliases}
            onChange={(event) => onAliasesChange(event.target.value)}
            placeholder="main, east side"
          />
        </div>
      </div>
      {onReservationsEnabledChange && (
        <div className="mt-4 space-y-3">
          <label className="flex items-start gap-3 text-sm text-gray-800 dark:text-gray-100">
            <input
              type="checkbox"
              checked={member.reservationsEnabled}
              onChange={(event) =>
                onReservationsEnabledChange(event.target.checked)
              }
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span>
              Accepting reservations. Turn off for a seasonal closure: callers
              to the other numbers won't be asked to pick this location, and can
              still look up or cancel existing reservations here. This
              location's own number keeps its normal settings.
            </span>
          </label>
          {!member.reservationsEnabled && (
            <div>
              <Label>Closure notice</Label>
              <Input
                type="text"
                value={member.unavailableNotice}
                onChange={(event) =>
                  onUnavailableNoticeChange?.(event.target.value)
                }
                placeholder="Our Lakeside location is closed for the season and reopens in April."
              />
            </div>
          )}
        </div>
      )}
      {readiness && !readiness.ready && (
        <p className="mt-3 text-sm text-error-600">
          Not ready to activate: {readiness.blockers.join(', ')}.
        </p>
      )}
    </div>
  );
}
